import { useId } from 'react';

import { InfoHint } from './InfoHint.tsx';
import { useI18n } from '~/i18n/index.tsx';

/**
 * One field, in both languages, side by side.
 *
 * Side by side rather than behind language tabs, deliberately. Every one of these is a *pair* —
 * an Arabic name and an English name for the same thing — and the commonest mistake is filling
 * one and forgetting the other. Tabs hide exactly the half you forgot; two boxes make the empty
 * one obvious while you are looking at the full one.
 *
 * The Arabic box carries `lang` and `dir` of its own so it is typed and rendered right-to-left
 * whichever language the panel itself is in.
 *
 * One label per field. The language names used to sit over each box as a second row of labels,
 * and "Name" over "Arabic" over a box read as two headings for one thing. They are now worn
 * inside each box at its reading end, as a tag — still each box's `<label>`, so it keeps its name.
 * Each side takes its box's direction, so "the reading end" is the end of the language typed there.
 */
export function BilingualField({
  label,
  ar,
  en,
  onArChange,
  onEnChange,
  multiline = false,
  rows = 3,
  hint,
  error,
  required = false,
}: {
  label: string;
  ar: string;
  en: string;
  onArChange: (value: string) => void;
  onEnChange: (value: string) => void;
  multiline?: boolean;
  rows?: number;
  hint?: string;
  error?: string;
  required?: boolean;
}) {
  const { t } = useI18n();
  const id = useId();

  const common = {
    className: multiline ? 'awj-textarea' : 'awj-input',
    'aria-invalid': error ? true : undefined,
    // Both boxes are described by the note, so it is heard on reaching either of them.
    'aria-describedby': hint ? `${id}-hint` : undefined,
  } as const;

  return (
    <div className={`bilingual ${multiline ? 'bilingual--multiline' : ''}`}>
      <span className="bilingual__head">
        <span className="bilingual__label" id={`${id}-label`}>
          {label}
          {required ? <span className="required-mark" aria-hidden="true">*</span> : null}
        </span>
        {hint ? <InfoHint text={hint} id={`${id}-hint`} /> : null}
      </span>

      <div className="bilingual__pair" role="group" aria-labelledby={`${id}-label`}>
        <div className="bilingual__side" dir="rtl">
          <label className="bilingual__lang" htmlFor={`${id}-ar`}>
            {t('lang.arabic')}
          </label>
          {multiline ? (
            <textarea
              {...common}
              id={`${id}-ar`}
              lang="ar"
              dir="rtl"
              rows={rows}
              value={ar}
              onChange={(event) => onArChange(event.target.value)}
            />
          ) : (
            <input
              {...common}
              id={`${id}-ar`}
              lang="ar"
              dir="rtl"
              value={ar}
              onChange={(event) => onArChange(event.target.value)}
            />
          )}
        </div>

        <div className="bilingual__side" dir="ltr">
          <label className="bilingual__lang" htmlFor={`${id}-en`}>
            {t('lang.english')}
          </label>
          {multiline ? (
            <textarea
              {...common}
              id={`${id}-en`}
              lang="en"
              dir="ltr"
              rows={rows}
              value={en}
              onChange={(event) => onEnChange(event.target.value)}
            />
          ) : (
            <input
              {...common}
              id={`${id}-en`}
              lang="en"
              dir="ltr"
              value={en}
              onChange={(event) => onEnChange(event.target.value)}
            />
          )}
        </div>
      </div>

      {error ? (
        <p className="bilingual__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
